import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, finalize } from 'rxjs/operators';
import { BaseUrlService } from '../base-url.service';
import { AuthenticationService } from './authentication.service';

@Injectable()
export class TokenInterceptorService<T> implements HttpInterceptor {
  public api: string;
  public identity: string;
  private ignoreRoutes: Array<string> = [];
  public refreshTokenURL: string;

  // FIXED: Better refresh state management
  private _refreshTokenSubject = new BehaviorSubject<boolean>(false);
  private _isRefreshingToken = false;
  public cancelPendingRequests$ = new Subject<void>();

  constructor(
    private authenticationService: AuthenticationService,
    private baseUrlService: BaseUrlService
  ) {
    this.api = this.baseUrlService.getApiBaseURL();
    this.identity = this.baseUrlService.getIdentityBaseURL();
    this.refreshTokenURL = `${this.identity}api/master/refreshToken`;
    this.setupIgnoreRoutes();
  }

  private setupIgnoreRoutes(): void {
    this.ignoreRoutes = [
      `${this.identity}api/master/acquireToken`,
      `${this.identity}api/master/acquireTokenTfaCheck`,
      `${this.identity}api/master/validateUserOtp`,
      `${this.identity}api/master/resendOtp`,
      `${this.identity}api/account/reset-password`,
      `${this.identity}api/account/reset-password-check`,
      `${this.api}api/account/user/saveUserName`,
      `${this.api}api/tpi/customer/visit/save`,
      `${this.identity}api/account/user/access-logout`,
      `${this.api}api/notifications/fcmToken/delete`,
      `${this.api}api/tpi/external-sales-agents/get`,
      `${this.identity}api/organization/org-logo/get`,
      `${this.api}api/account/properties/get`,
      `${this.api}api/common/configurations`,
      `${this.api}api/tpi/website/lead`,
      `${this.api}api/tpi/property-logo/get`,
      `${this.api}api/tpi/property-quotation/save`,
      `${this.api}api/tpi/external-cp-form/save`,
      `${this.api}api/tpi/external-form/send-otp`,
      `${this.api}api/tpi/external-form/validate-otp`,
      `${this.api}api/account/aliasName`,
      `${this.api}api/account/user-reset-password-otp-send`,
      `${this.api}api/account/user-reset-password-otp-verify`,
      `${this.api}api/account/reset-password`,
      `${this.api}api/account/reset-password-new`
    ];
  }

  public intercept(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Skip non-API routes or ignored routes
    if (!this.startsWithAPI(req.url) || this.isIgnoredRoute(req)) {
      return next.handle(req);
    }

    // Handle refresh token URL specially to prevent infinite loops
    if (this.isSameRoute(req.url, this.refreshTokenURL)) {
      return this.handleError(next.handle(req));
    }

    // FIXED: Check token expiry and handle refresh with better logic
    return this.handleRequestWithTokenCheck(req, next);
  }

  private handleRequestWithTokenCheck(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // If already refreshing, queue this request
    if (this._isRefreshingToken) {
      return this.waitForTokenRefresh(req, next);
    }

    // Check if token needs refresh
    if (this.authenticationService.isUserExpired()) {
      return this.handleTokenRefresh(req, next);
    }

    // Token is valid, proceed normally
    return this.handleError(this.$addTokenToHeader(req, next));
  }

  private handleTokenRefresh(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // FIXED: Double-check to prevent race conditions
    if (this._isRefreshingToken) {
      return this.waitForTokenRefresh(req, next);
    }

    console.log('Starting token refresh process');
    this._isRefreshingToken = true;
    this._refreshTokenSubject.next(true);

    return this.authenticationService.refreshToken().pipe(
      switchMap(() => {
        console.log('Token refreshed successfully, proceeding with request');
        // Token refreshed successfully, proceed with original request
        return this.$addTokenToHeader(req, next);
      }),
      catchError(error => {
        // Token refresh failed
        console.error('Token refresh failed in interceptor:', error);
        this.authenticationService.logOut();
        return throwError(() => error);
      }),
      finalize(() => {
        // Always reset the refresh state
        console.log('Token refresh process completed');
        this._isRefreshingToken = false;
        this._refreshTokenSubject.next(false);
      })
    );
  }

  private waitForTokenRefresh(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    console.log('Waiting for token refresh to complete');

    return this._refreshTokenSubject.pipe(
      filter(isRefreshing => !isRefreshing), // Wait until refresh is done
      take(1),
      switchMap(() => {
        // Check if we still have a valid token after refresh
        if (!this.authenticationService.isAuthenticated) {
          return throwError(() => new Error('Authentication failed'));
        }

        // Proceed with the request using the new token
        return this.$addTokenToHeader(req, next);
      })
    );
  }

  private startsWithAPI(url: string): boolean {
    return url.startsWith(this.api) || url.startsWith(this.identity);
  }

  private isIgnoredRoute(req: HttpRequest<T>): boolean {
    return this.ignoreRoutes.some(route => this.isSameRoute(req.url, route));
  }

  private isSameRoute(req: string, url: string): boolean {
    return req.split('?')[0] === url;
  }

  private handleError(
    res: Observable<HttpEvent<unknown>>
  ): Observable<HttpEvent<unknown>> {
    return res.pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            case 401:
              // FIXED: Only logout if not already refreshing
              if (!this._isRefreshingToken) {
                console.log('401 Unauthorized - Logging out');
                this.authenticationService.logOut();
              }
              break;
            default:
              return throwError(() => err);
          }
        }
        return throwError(() => err);
      })
    );
  }

  protected $addTokenToHeader(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return this.authenticationService.$token.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        const request = this.addTokenToHeader(req, token);
        return this.handleError(next.handle(request));
      })
    );
  }

  protected addTokenToHeader(
    req: HttpRequest<T>,
    token: {
      token: string | null;
      apiKey: string | null;
    } | null
  ): HttpRequest<T> {
    return req.clone({
      setHeaders: {
        AUTHORIZATION: `Bearer ${token?.token}`,
        'X-API-KEY': `${token?.apiKey}`
      }
    });
  }
}
